import { Box, Button as MuiButton, IconButton as MuiIconButton } from "@mui/material";
import React from "react";
import aderantTheme from "../Theme/AderantTheme";
const palette = aderantTheme.palette;
/**
 * Custom Button control.
 * @param props @see ButtonProps
 *
 */
export function Button(props) {
    const { onClick, text, color, variant, size, startIcon, style, endIcon, rounded, iconButton, disabled, title, "aria-label": ariaLabel, "aria-controls": ariaControls, "aria-haspopup": ariaHasPopup, "aria-expanded": ariaExpanded, className, muiButtonRef, showActiveState } = props;
    //Throw if no icon and text prop specified
    if (!iconButton && !text && !startIcon && !endIcon) {
        throw `Either Text or Icon parameters must be supplied.`;
    }
    if (iconButton && !startIcon) {
        throw `StartIcon parameter is required for an Icon button.`;
    }
    //Map large icon button to medium size as large is not supported by material ui
    const actualSize = iconButton ? (size === "large" ? "medium" : size) : size;
    const buttonStyles = {
        containedPrimary: {
            color: palette.primary.contrastText,
            backgroundColor: palette.primary.main,
            ":disabled": {
                backgroundColor: showActiveState ? palette.background.active : palette.primary.main
            }
        },
        containedSecondary: {
            color: palette.secondary.contrastText,
            backgroundColor: palette.secondary.main,
            ":disabled": {
                backgroundColor: showActiveState ? palette.background.active : palette.secondary.main
            }
        },
        outlinedSecondary: {
            border: `solid 1px ${palette.secondary.main}`
        },
        plainPrimary: {
            border: `solid 1px transparent`,
            boxShadow: "none",
            ":hover": {
                boxShadow: "none"
            }
        },
        plainSecondary: {
            border: `solid 1px transparent`,
            boxShadow: "none",
            ":hover": {
                boxShadow: "none"
            }
        },
        showActiveState: {
            backgroundColor: palette.background.active,
            color: palette.primary.contrastText,
            border: `solid 1px ${palette.border.focus}`,
            //Overrides hover styling in touch-devices
            "@media (hover: none)": {
                ":hover": {
                    backgroundColor: palette.background.active,
                    color: palette.primary.contrastText,
                    border: `solid 1px ${palette.border.focus}`
                }
            }
        }
    };
    const sxProps = ({ palette, ...theme }) => ({
        fontSize: "0.8125rem",
        borderRadius: rounded ? "100px" : "6px",
        border: `solid 1px ${palette.primary.main}`,
        boxShadow: "1px 1px 2px 0 rgba(0, 0, 0, 0.2)",
        [theme.breakpoints.down("sm")]: {
            margin: 0,
            ...(iconButton ? { width: "100%" } : {})
        },
        ":focus-visible": {
            backgroundColor: palette.background.focus,
            color: palette.primary.main,
            border: `solid 1px ${palette.border.focus}`,
            boxShadow: `0 0 0 1px ${palette.border.focus}`
        },
        "@media (hover: hover)": {
            ":hover": {
                backgroundColor: palette.background.hover,
                color: palette.secondary.contrastText
            }
        },
        ":hover": {
            boxShadow: "1px 1px 2px 0 rgba(0, 0, 0, 0.2)"
        },
        ...(variant !== "plain" && {
            ":active": {
                color: palette.common.white,
                backgroundColor: palette.background.active,
                border: `solid 1px ${palette.border.active}`
            }
        }),
        ...(variant === "contained" ? (color === "primary" ? buttonStyles.containedPrimary : buttonStyles.containedSecondary) : {}),
        ...(variant === "outlined" ? (color === "primary" ? buttonStyles.outlinedPrimary : buttonStyles.outlinedSecondary) : {}),
        ...(variant === "plain" ? (color === "primary" ? buttonStyles.plainPrimary : buttonStyles.plainSecondary) : {}),
        ...(size === "small" && iconButton ? { padding: "3px" } : {}),
        ...(size === "large" && !iconButton ? { padding: "12px 22px" } : {}),
        ...(showActiveState && buttonStyles.showActiveState),
        ...(disabled ? { opacity: "0.4" } : {})
    });
    return (React.createElement(React.Fragment, null, iconButton ? (React.createElement(Box, { style: style },
        React.createElement(MuiIconButton, { id: props.id, disableRipple: variant === "plain" ? false : true, "data-testid": "button-icon", color: color, className: className, title: title, disabled: disabled, onClick: onClick, size: actualSize, "aria-label": ariaLabel ?? text, "aria-controls": ariaControls, "aria-expanded": ariaExpanded, "aria-haspopup": ariaHasPopup, ref: muiButtonRef, disableFocusRipple: variant === "plain" ? false : true, sx: sxProps }, startIcon))) : (React.createElement(Box, { style: style },
        React.createElement(MuiButton, { id: props.id, disableRipple: true, className: className, sx: sxProps, "data-testid": "button-standard", title: title, disabled: disabled, startIcon: startIcon, endIcon: endIcon, variant: props.variant === "plain" ? "text" : props.variant, color: color, size: actualSize, onClick: onClick, "aria-label": ariaLabel ?? text, "aria-controls": ariaControls, "aria-expanded": ariaExpanded, "aria-haspopup": ariaHasPopup, ref: muiButtonRef }, text)))));
}
//Prop defaults.
Button.defaultProps = {
    disabled: false,
    variant: "contained",
    color: "primary",
    text: "",
    rounded: false,
    size: "small",
    iconButton: false,
    showActiveState: false
};
